import { categoryById } from '../routes/categorySlugs';

/** Category color CSS variable mapping (for UI components) */
export const categoryColors: Record<string, string> = {
  mercatino: 'var(--color-cat-mercatino)',
  sagra: 'var(--color-cat-sagra)',
  fiera: 'var(--color-cat-fiera)',
  musica: 'var(--color-cat-musica)',
  bambini: 'var(--color-cat-bambini)',
  sport: 'var(--color-cat-sport)',
  cinema_teatro: 'var(--color-cat-cinema)',
  tradizione: 'var(--color-cat-tradizione)',
  festivita: 'var(--color-cat-festivita)',
  rievocazione: 'var(--color-cat-rievocazione)',
  arte: 'var(--color-cat-arte)',
  altro: 'var(--color-cat-altro)',
};

/** Category hex colors for contexts where CSS variables are unavailable (e.g. Leaflet SVG markers) */
export const categoryHexColors: Record<string, string> = {
  mercatino: '#E86A33',
  sagra: '#C84B4B',
  fiera: '#4A6FA5',
  musica: '#7B5EA7',
  bambini: '#3AAFA9',
  sport: '#5B9A4D',
  cinema_teatro: '#2563EB',
  tradizione: '#D97706',
  festivita: '#DB2777',
  rievocazione: '#92400E',
  arte: '#7C2D12',
  altro: '#6B6560',
};

/** Category illustration filenames for sponsored cards */
export const categoryIllustrations: Record<string, string> = {
  mercatino: 'mercatino.png',
  sagra: 'sagra.png',
  fiera: 'fiera.png',
  musica: 'musica.png',
  bambini: 'famiglia.png',
  sport: 'sport.png',
  cinema_teatro: 'cinema.png',
  tradizione: 'tradizione.png',
  festivita: 'festivita.png',
  rievocazione: 'rievocazione.png',
  arte: 'arte.png',
  altro: 'altro.png',
};

/** Category corner vignette filenames for sponsored cards (test: 3 categories) */
export const categoryPatterns: Record<string, string> = {
  bambini: 'famiglia.png',
  festivita: 'festivita.png',
  altro: 'altro.png',
};

/** Category fallback photo filenames (realistic photos for cards without sponsor images) */
export const categoryFallbacks: Record<string, string> = {
  mercatino: 'mercatino.jpg',
  sagra: 'sagra.jpg',
  fiera: 'fiera.jpg',
  musica: 'musica.jpg',
  bambini: 'bambini.jpg',
  sport: 'sport.jpg',
  cinema_teatro: 'cinema_teatro.jpg',
  tradizione: 'tradizione.jpg',
  festivita: 'festivita.jpg',
  rievocazione: 'rievocazione.jpg',
  arte: 'arte.jpg',
  altro: 'altro.jpg',
};

/**
 * Singular display labels — describe ONE event (badge on a card, chip on the
 * detail modal, category select of an event form). For anything that lists or
 * navigates categories use `categoryShortLabels` (tight UI) or
 * `categoryLongLabel` (headings, category-page links, desktop home tiles).
 */
export const categoryLabels: Record<string, string> = {
  mercatino: 'Mercatino',
  sagra: 'Sagra',
  fiera: 'Fiera',
  musica: 'Musica',
  bambini: 'Bambini',
  sport: 'Sport',
  cinema_teatro: 'Spettacoli',
  tradizione: 'Tradizione',
  festivita: 'Festività',
  rievocazione: 'Rievocazione',
  arte: 'Mostre',
  altro: 'Evento',
};

/**
 * Short category names — the single source for space-constrained navigation
 * (home chips, filter pills/sheets). Approved table: DB id → short name.
 */
export const categoryShortLabels: Record<string, string> = {
  mercatino: 'Mercatini',
  sagra: 'Sagre',
  fiera: 'Fiere',
  arte: 'Mostre',
  musica: 'Musica',
  bambini: 'Bambini',
  sport: 'Sport',
  cinema_teatro: 'Spettacoli',
  festivita: 'Festività',
  tradizione: 'Tradizioni',
  rievocazione: 'Rievocazioni',
  altro: 'Altro',
};

/**
 * Long category name — page headings, links to category pages, and the
 * desktop homepage tiles (they have room). Read from `CATEGORY_SLUGS`, the
 * same map the indexed category pages use, so heading text and link targets
 * can never drift apart. `altro` has no category page (C2) and falls back to
 * its short name.
 */
export function categoryLongLabel(id: string): string {
  return categoryById(id)?.label ?? categoryShortLabels[id] ?? id;
}
